import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import Images from '../Images';

const slides = [
  {
    id: 0,
    tag: 'BULK SMS',
    heading: 'Affordable Bulk SMS for your business',
    accentColor: '#f7941d',
    primaryBtn: { label: 'Get Started', link: 'https://quicksmsgh.com/register' },
    image: Images.bulkb,
    imageAlt: 'Bulk SMS illustration',
  },
  {
    id: 1,
    tag: 'Email Marketing',
    heading: 'Reach your audience with powerful e-mail marketing',
    accentColor: '#F7941D',
    primaryBtn: { label: 'Get Started', link: null },
    image: Images.emailb,
    imageAlt: 'Email marketing illustration',
  },
  {
    id: 2,
    tag: 'E-voting',
    heading: 'Power Your Elections with Secure & Reliable Online Voting',
    accentColor: '#f7941d',
    primaryBtn: { label: 'Get Started', link: null },
    image: Images.voteb,
    imageAlt: 'evoting illustration',
  },
  {
    id: 3,
    tag: 'Trivia Game',
    heading: 'Challenge your mind and win big in our Trivia Game',
    accentColor: '#f7941d',
    primaryBtn: { label: 'Play Now', link: 'https://www.whowantstoplaygh.com/' },
    image: Images.playb,
    imageAlt: 'Trivia game illustration',
  },
  {
    id: 4,
    tag: 'VOICE BROADCAST',
    heading: 'Send personalised voice calls to your audience in seconds',
    accentColor: '#f7941d',
    primaryBtn: { label: 'Get Started', link: null },
    image: Images.voiceb,
    imageAlt: 'Voice broadcast illustration',
  },
];

const AUTOPLAY_DELAY = 5500;

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: (dir) => ({
    x: dir > 0 ? '-100%' : '100%',
    opacity: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const textVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: d, ease: 'easeOut' },
  }),
};

const HeroSection = () => {
  const [[current, direction], setPage] = useState([0, 1]);
  const [paused, setPaused] = useState(false);

  const paginate = useCallback((dir) => {
    setPage(([curr]) => [(curr + dir + slides.length) % slides.length, dir]);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => paginate(1), AUTOPLAY_DELAY);
    return () => clearInterval(t);
  }, [paused, paginate]);

  const slide = slides[current];
  const hasLink = Boolean(slide.primaryBtn.link);

  return (
    <section className="relative mt-12 md:mt-16 flex items-center overflow-hidden px-4 sm:px-6 lg:px-8 py-4 md:py-8">
      <div
        className="relative w-full max-w-[86rem] mx-auto rounded-2xl md:rounded-[2rem] overflow-hidden
                   min-h-[500px] sm:min-h-[540px] md:min-h-[600px] lg:min-h-[680px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={current}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0"
            style={{ backgroundColor: slide.bgColor }}
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${slide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0" />
            </div>

            {/* Content */}
            <div
              className="
                relative z-20 h-full
                flex flex-col justify-center
                px-5 sm:px-8 md:px-10 lg:px-16
                pt-2 pb-8 sm:pt-4 sm:pb-10 lg:py-0
                w-full lg:w-3/5
                text-center lg:text-left
              "
            >
              {/* Tag */}
              <motion.div
                variants={textVariants}
                initial="hidden"
                animate="visible"
                custom={0.05}
                className="mb-3 sm:mb-4 md:mb-5"
              >
                <span className="inline-block border border-white/50 text-white text-[10px] sm:text-xs font-bold tracking-[0.15em] uppercase px-3 sm:px-4 py-1 sm:py-1.5 rounded-full">
                  {slide.tag}
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1
                variants={textVariants}
                initial="hidden"
                animate="visible"
                custom={0.15}
                className="
                  font-extrabold text-white whitespace-pre-line leading-tight
                  text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[3.2rem]
                  lg:leading-[1.15] mb-2 sm:mb-3 md:mb-4
                "
              >
                {slide.heading}
              </motion.h1>

              {/* Accent */}
              {slide.accent && (
                <motion.p
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  custom={0.28}
                  className="text-sm sm:text-base md:text-lg lg:text-xl font-medium mb-5 sm:mb-7 md:mb-10"
                  style={{ color: slide.accentColor }}
                >
                  {slide.accent}
                </motion.p>
              )}

              {/* CTA */}
              <motion.div
                variants={textVariants}
                initial="hidden"
                animate="visible"
                custom={0.4}
                className="flex justify-center lg:justify-start"
              >
                {hasLink ? (
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: '0 8px 28px rgba(0,0,0,0.3)' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.open(slide.primaryBtn.link, '_blank')}
                    className="
                      bg-[#F7941D] hover:bg-[#E08010] text-white font-bold
                      px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4
                      rounded-lg transition-all duration-300
                      inline-flex items-center gap-2 shadow-lg
                      text-sm sm:text-base cursor-pointer
                    "
                  >
                    {slide.primaryBtn.label}
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </motion.button>
                ) : (
                  <button
                    disabled
                    className="
                      bg-white/20 text-white/50 font-bold
                      px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4
                      rounded-lg
                      inline-flex items-center gap-2
                      text-sm sm:text-base cursor-not-allowed
                      border border-white/20
                    "
                  >
                    {slide.primaryBtn.label}
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                )}
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Arrows */}
        <button
          onClick={() => paginate(-1)}
          aria-label="Previous slide"
          className="hidden sm:block absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-30
                     bg-black/30 hover:bg-black/55 text-white rounded-full p-2 sm:p-2.5
                     transition-all duration-200 backdrop-blur-sm"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <button
          onClick={() => paginate(1)}
          aria-label="Next slide"
          className="hidden sm:block absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-30
                     bg-black/30 hover:bg-black/55 text-white rounded-full p-2 sm:p-2.5
                     transition-all duration-200 backdrop-blur-sm"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setPage([i, i > current ? 1 : -1])}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-5 sm:w-7 h-2 sm:h-3 bg-[#F7941D]'
                  : 'w-2 sm:w-3 h-2 sm:h-3 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;